/**
 * State Consistency Rule — expressions and setState targets must use consistent paths.
 */

import type { FeatureRule, RuleViolation, BlockData } from "../types"

function collectExpressions(obj: unknown): string[] {
  const exprs: string[] = []
  const str = JSON.stringify(obj ?? {})
  const pattern = /#\{([^}]+)\}/g
  let m
  while ((m = pattern.exec(str)) !== null) {
    exprs.push(m[1])
  }
  return exprs
}

function collectSetStateTargets(flow: Record<string, unknown>): string[] {
  const targets: string[] = []
  const str = JSON.stringify(flow)
  // Match setState target values
  const pattern = /"target"\s*:\s*"([^"]+)"/g
  let m
  while ((m = pattern.exec(str)) !== null) {
    targets.push(m[1])
  }
  return targets
}

export const stateConsistencyRule: FeatureRule = {
  id: "state-consistency",
  name: "State Path Consistency",
  detect: () => true, // Always check
  check: (data) => {
    const violations: RuleViolation[] = []

    // Collect all #{...} expressions from UI
    const uiExprs = collectExpressions(data.ui)
    // Collect all setState targets from flows
    const targets = collectSetStateTargets(data.flow)

    // Check that setState targets are read somewhere in UI
    for (const target of targets) {
      // Normalize: remove # prefix if present
      const normalized = target.startsWith("#") ? target.slice(1) : target
      // Check if any UI expression reads this path
      const isRead = uiExprs.some((expr) => expr.includes(normalized) || normalized.includes(expr))
      if (!isRead && !normalized.startsWith("_results") && !normalized.startsWith("appData.lastAction")) {
        violations.push({
          rule: "state-write-no-read",
          level: "info",
          message: `setState writes to '${normalized}' but no UI expression reads it.`,
          file: "flow",
        })
      }
    }

    return violations
  },
}
